/**
 * License Management. Doc: docs/overall-plan/02-MANGO-PERSONAL/license-management
 * Full CRUD: GET/POST/PUT/DELETE /api/v1/license-management/licenses.
 */
import React, { useEffect, useState } from 'react';
import { licenseManagementApi, type License } from '../services/api';
import { askConfirm } from 'shared-ui';

const LicenseManagement: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleEdit = (lic: License) => {
    setEditingId(lic.id);
    setEditName(lic.name);
    setEditType(lic.type || '');
    setEditExpiryDate(lic.expiryDate ? new Date(lic.expiryDate).toISOString().slice(0, 10) : '');
  };

  const handleUpdate = async () => {
    if (!editingId || !editName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await licenseManagementApi.updateLicense(editingId, {
        name: editName.trim(),
        type: editType.trim() || undefined,
        expiryDate: editExpiryDate || undefined,
      });
      if (res?.success) {
        setEditingId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to update');
    } catch {
      setError('Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await askConfirm({ title: 'Remove this license?', confirmLabel: 'Remove', danger: true }))) return;
    setDeleting(id);
    setError(null);
    try {
      const res = await licenseManagementApi.deleteLicense(id);
      if (res?.success) {
        if (editingId === id) setEditingId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to delete');
    } catch {
      setError('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await licenseManagementApi.createLicense({
        name: newName.trim(),
        type: newType.trim() || undefined,
        expiryDate: newExpiryDate || undefined,
      });
      if (res?.success) {
        setNewName('');
        setNewType('');
        setNewExpiryDate('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add license');
    } catch {
      setError('Failed to add license');
    } finally {
      setSubmitting(false);
    }
  };

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await licenseManagementApi.getLicenses();
      if (res?.success) {
        setLicenses(Array.isArray(res?.data) ? res.data : []);
        setMessage(res?.message ?? null);
      } else setError(res?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>📜 License Management</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Store and renew licenses and certifications
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#2c3e50', cursor: 'pointer', fontSize: '14px' }}>Add License</button>
            <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>
      </div>
      {showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input type="text" placeholder="License name" value={newName} onChange={(e) => setNewName(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="text" placeholder="Type (optional)" value={newType} onChange={(e) => setNewType(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="date" placeholder="Expiry date (optional)" value={newExpiryDate} onChange={(e) => setNewExpiryDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAdd} disabled={submitting || !newName.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Add'}</button>
            <button type="button" onClick={() => { setShowAdd(false); setNewName(''); setNewType(''); setNewExpiryDate(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {editingId && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '2px solid #3498db' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Edit license</h3>
          <input type="text" placeholder="License name" value={editName} onChange={(e) => setEditName(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="text" placeholder="Type (optional)" value={editType} onChange={(e) => setEditType(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="date" placeholder="Expiry date" value={editExpiryDate} onChange={(e) => setEditExpiryDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleUpdate} disabled={submitting || !editName.trim()} style={{ padding: '8px 16px', background: '#3498db', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => { setEditingId(null); setEditName(''); setEditType(''); setEditExpiryDate(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && !error && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>}
      {licenses.length === 0 && !error && !showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          No licenses yet. Add a license above.
        </div>
      )}
      {licenses.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {licenses.map((l) => (
            <li key={l.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div>
                <strong>{l.name}</strong>
                {l.type && <span style={{ marginLeft: '8px', color: '#64748b' }}>- {l.type}</span>}
                {l.expiryDate && <span style={{ marginLeft: '8px', fontSize: '12px' }}>expires {new Date(l.expiryDate).toLocaleDateString()}</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={() => handleEdit(l)} style={{ padding: '6px 12px', background: '#3498db', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                <button type="button" onClick={() => handleDelete(l.id)} disabled={deleting === l.id} style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: deleting === l.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}>{deleting === l.id ? '…' : 'Delete'}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LicenseManagement;
